import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft,
  ArrowRight,
  Calendar,
  Coins,
  Clock,
  Loader2,
  Stethoscope,
} from "lucide-react";
import { usePageMeta } from "../hooks/usePageMeta";
import ShareButtons from "../components/ShareButtons";
import {
  getStudyCountries,
  selectCompensation,
} from "../lib/countries";
import { formatClosingDate } from "../lib/dates";
import CompensationStack from "../components/CompensationStack";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Canonical, indexable page for a single study. Search engines crawl this
 * (it's in the sitemap), so it ships with its own <title>, description and
 * MedicalStudy JSON-LD. The interactive "Express interest" flow still lives
 * on the modal triggered from /patient/studies and /caregiver/studies , this
 * page links there.
 */
export default function StudyDetailPage() {
  const { study_id } = useParams();
  const [study, setStudy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/studies/${study_id}`);
        if (!cancelled) {
          setStudy(data);
          setLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          setNotFound(true);
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [study_id]);

  const jsonLd = study
    ? {
        "@context": "https://schema.org",
        "@type": "MedicalStudy",
        name: study.title,
        description:
          study.description ||
          `Healthcare research study about ${study.condition || "various conditions"} on MediTalk.`,
        url: `https://www.meditalk.world/studies/${study.id}`,
        studyLocation: (() => {
          const list = getStudyCountries(study);
          if (list.length === 0) {
            return study.country
              ? { "@type": "AdministrativeArea", name: study.country }
              : undefined;
          }
          if (list.length === 1) {
            return { "@type": "AdministrativeArea", name: list[0].name };
          }
          return list.map((c) => ({
            "@type": "AdministrativeArea",
            name: c.name,
          }));
        })(),
        healthCondition: study.condition
          ? {
              "@type": "MedicalCondition",
              name: study.condition,
            }
          : undefined,
        status: study.is_active ? "Recruiting" : "Closed",
        sponsor: {
          "@type": "Organization",
          name: "MediTalk",
          url: "https://www.meditalk.world/",
        },
      }
    : null;

  usePageMeta({
    path: `/studies/${study_id}`,
    title: study
      ? `${study.title} , Paid healthcare research study · MediTalk`
      : "Study , MediTalk",
    description: study
      ? `${study.condition ? `${study.condition} study. ` : ""}${(study.description || "").slice(0, 155)}`.trim()
      : "Healthcare research study on MediTalk.",
    jsonLd,
  });

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 size={28} className="animate-spin text-[var(--lime-dark)]" />
      </div>
    );
  }

  if (notFound || !study) {
    return (
      <div className="bg-white">
        <section className="bg-[var(--dark-bg)] grain-dark py-16 md:py-24">
          <div className="container-main max-w-2xl text-center">
            <h1 className="font-['Outfit'] font-bold text-white text-3xl md:text-4xl mb-4">
              We couldn't find that study
            </h1>
            <p className="text-white/80 mb-8">
              It may have closed or moved. Browse all live studies instead.
            </p>
            <Link to="/patient/studies" className="btn-primary">
              Browse open studies
            </Link>
          </div>
        </section>
      </div>
    );
  }

  const audience = study.study_type === "caregiver" ? "caregiver" : "patient";
  const signUpPath = `/${audience}/sign-up`;
  const studiesPath = `/${audience}/studies`;
  const countries = getStudyCountries(study);
  const { rows: compRows, fallback: compFallback } = selectCompensation(
    study,
    null,
  );

  return (
    <div className="bg-white">
      {/* Hero , DARK */}
      <section className="bg-[var(--dark-bg)] grain-dark relative overflow-hidden">
        <div
          aria-hidden="true"
          className="absolute -top-32 -left-24 w-[420px] h-[420px] rounded-full bg-[var(--lime)]/10 blur-3xl pointer-events-none"
        />
        <div className="container-main py-12 md:py-16 relative z-10 max-w-3xl">
          <Link
            to={studiesPath}
            className="inline-flex items-center gap-1.5 text-sm text-white/80 hover:text-white transition-colors mb-5"
            data-testid="study-back-link"
          >
            <ArrowLeft size={14} />
            All {audience} studies
          </Link>

          <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] font-semibold text-[var(--lime)] mb-3">
            <span className="w-6 h-px bg-[var(--lime)]" />
            {study.format || study.study_type || "Healthcare study"}
            {study.condition ? ` · ${study.condition}` : ""}
          </span>

          <h1
            className="font-['Outfit'] font-bold text-white tracking-tight text-3xl md:text-5xl mb-4 leading-[1.05]"
            data-testid="study-detail-title"
          >
            {study.title}
          </h1>

          {study.project_number && (
            <p
              className="text-[11px] uppercase tracking-[0.14em] font-semibold text-white/55 -mt-2 mb-4"
              data-testid="study-detail-ref"
            >
              Ref · {study.project_number}
            </p>
          )}

          {study.description && (
            <p className="text-base md:text-lg text-white/80 leading-relaxed max-w-2xl">
              {study.description.split("\n")[0]}
            </p>
          )}

          {/* Share row , patient + caregiver communities heavily share
              relevant studies in WhatsApp groups and Facebook support
              groups. One-tap share collapses several steps of friction. */}
          <div className="mt-6">
            <ShareButtons
              url={`https://www.meditalk.world/studies/${study.id}`}
              text={`${study.title} , paid research on MediTalk`}
              source={`study-${study.id}`}
              variant="dark"
            />
          </div>
        </div>
      </section>

      {/* Facts strip , WHITE. "Where" intentionally dropped, country
          info lives inside the stacked Thank-you panel instead. */}
      <section className="border-b border-[var(--light-border)]">
        <div className="container-main max-w-3xl py-8 md:py-10">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <Fact
              Icon={Clock}
              label="Time commitment"
              value={study.time_commitment || "See details"}
            />
            <Fact
              Icon={Stethoscope}
              label="Confirmation"
              value={diagnosisLabel(study.diagnosis_confirmation) || "Screening questions"}
            />
            <Fact
              Icon={Calendar}
              label="Closes"
              value={formatClosingDate(study.end_date) || "Rolling"}
            />
          </div>

          {/* Thank-you / compensation , stacked rows, one per country.
              Spec: flag + country abbreviation + amount on each row. */}
          <div
            className="mt-6 rounded-xl border border-[var(--lime)]/40 bg-[var(--lime)]/[0.08] p-5"
            data-testid="study-detail-multi-country-block"
          >
            <p className="text-xs uppercase tracking-wider font-semibold text-[var(--text-muted)] mb-3 flex items-center gap-1.5">
              <Coins size={13} />
              Thank-you
              {countries.length >= 2 && (
                <span className="ml-1 text-[var(--olive)] font-medium normal-case tracking-normal">
                  , {countries.length} countries
                </span>
              )}
            </p>
            <CompensationStack
              rows={compRows}
              fallback={compFallback}
              variant="panel"
              testId="study-detail-compensation-stack"
            />
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="py-12 md:py-16">
        <div className="container-main max-w-3xl space-y-8">
          {study.description && (
            <div>
              <h2 className="font-['Outfit'] font-bold text-[var(--charcoal)] text-2xl md:text-3xl mb-3">
                About this study
              </h2>
              <p className="text-[15px] md:text-base text-[var(--text-muted)] leading-relaxed whitespace-pre-line">
                {study.description}
              </p>
            </div>
          )}

          {study.diagnosis_confirmation && (
            <div className="rounded-2xl border border-[var(--light-border)] bg-[var(--bone-soft)] p-5 md:p-6">
              <h3 className="inline-flex items-center gap-2 text-[15px] font-semibold text-[var(--charcoal)] mb-2">
                <Stethoscope
                  size={16}
                  className="text-[var(--lime-dark)]"
                />
                Eligibility check
              </h3>
              <p className="text-[14.5px] text-[var(--text-muted)] leading-relaxed">
                {study.diagnosis_confirmation === "physician_crf" &&
                  "Your diagnosis will be confirmed by your doctor via a short clinical form (no need to do this yourself)."}
                {study.diagnosis_confirmation === "self_plus_docs" &&
                  "You'll be asked to upload a brief proof of diagnosis (e.g. a prescription, hospital letter or test result)."}
                {study.diagnosis_confirmation === "self_reported" &&
                  "Self-reported eligibility , no diagnosis paperwork required."}
              </p>
            </div>
          )}

          {/* CTA */}
          <div className="rounded-2xl bg-[var(--dark-bg)] text-white p-6 md:p-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <p className="text-xs uppercase tracking-[0.18em] font-semibold text-[var(--lime)] mb-1">
                Ready to take part?
              </p>
              <h3 className="font-['Outfit'] font-bold text-white text-xl md:text-2xl">
                Express interest in this study
              </h3>
              <p className="text-white/75 text-[14.5px] mt-1 max-w-md">
                Tap below to be taken back to the studies page where you can
                share your details with the research team.
              </p>
            </div>
            <Link
              to={studiesPath}
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-[var(--lime)] text-[var(--dark-bg)] font-semibold text-sm hover:bg-[var(--lime-dark)] transition-all shadow-[0_8px_22px_-10px_rgba(196,247,75,0.7)] whitespace-nowrap"
              data-testid="study-detail-cta"
            >
              Open this study
              <ArrowRight size={16} />
            </Link>
          </div>

          {/* Not registered? */}
          <div className="border-t border-[var(--light-border)] pt-6">
            <p className="text-sm text-[var(--text-muted)] leading-relaxed">
              Not on MediTalk yet?{" "}
              <Link
                to={signUpPath}
                className="text-[var(--charcoal)] font-semibold underline decoration-[var(--lime)] decoration-2 underline-offset-4 hover:decoration-[var(--lime-dark)]"
              >
                Create your free {audience} profile
              </Link>{" "}
              in 3 minutes and we'll match you to studies like this.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}

const Fact = ({ Icon, label, value }) => (
  <div>
    <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.15em] font-semibold text-[var(--text-muted)] mb-1.5">
      <Icon size={13} className="text-[var(--lime-dark)]" />
      {label}
    </div>
    <div className="text-[14.5px] font-semibold text-[var(--charcoal)] leading-snug">
      {value}
    </div>
  </div>
);

const diagnosisLabel = (key) => {
  if (key === "self_plus_docs") return "Screening + documents";
  if (key === "physician_crf") return "Screening + physician confirmation";
  if (key === "self_reported") return "Screening questions";
  return null;
};
